import { RemoveYear, hasInErrorReason } from "./helpers.js";
import { PAYMENT_COMPONENT_IDS } from "./constants.js";
class ClaimsPaymentSearch {
    constructor() {
        this.format = (response) => {
            if (!response || typeof response !== "object") {
                return { claimsPaymentSearch: [], searchResultMetadata: {} };
            }
            const { searchResultMetaData, claims } = response;
            const { startIndex, totalResults, itemsPerPage, resultMetaData } = searchResultMetaData || {};
            const { timeTaken, _links } = resultMetaData || {};
            const searchResultMetadata = {
                startIndex,
                totalResults,
                itemsPerPage,
                timeTaken,
                links: _links,
            };
            const normalizeId = (id) => (id ?? "").trim().replace(/\s+/g, " ");
            const buildComponentMap = (components = []) => components.reduce((map, pc) => {
                const normalizedId = normalizeId(pc.componentId);
                if (!map[normalizedId]) {
                    map[normalizedId] = [];
                }
                map[normalizedId].push(pc);
                return map;
            }, {});
            const findComponent = (componentMap, ids = []) => {
                for (const id of ids) {
                    if (componentMap[id]) {
                        return componentMap[id];
                    }
                }
                return null;
            };
            const getAmt = (component) => {
                if (!component)
                    return null;
                return component.componentPaymentAmt;
            };
            const getMaxComponentInstances = (componentMap) => {
                return Math.max(1, ...Object.values(componentMap).map((arr) => arr.length));
            };
            const isDstComponent = (component = {}) => {
                const processType = component.processedTypeCd;
                return (typeof processType === "string" &&
                    processType.trim().toUpperCase() === "DST");
            };
            const mappedComponentIds = new Set(Object.values(PAYMENT_COMPONENT_IDS).flat());
            // TODO: Update Type for componentId
            const isMappedTableComponent = (component = {}) => mappedComponentIds.has(normalizeId(component.componentId));
            if (!Array.isArray(claims)) {
                return { claimsPaymentSearch: [], searchResultMetadata };
            }
            const claimsPaymentSearch = claims
                .filter((claim) => !hasInErrorReason(claim.claimProcessingStage, claim.claimStatusReason, claim.customClaimStatusReason, claim.claimStatusReasonDesc, claim.claimStatusReasonCdDesc, claim.customClaimStatusReasonCdDesc))
                .flatMap((claim) => {
                const { claimantDisplayName, claimantFirstName, claimantLastName, employeeId, taxIdentificationNumber, groupAgreementId, divisionId, eligibilityGroup, organizationUnitId, claimCoverageTypeCd, claimId, aliasClaimId, choiceCd, disabilityBeginDt, employeeWorkStateCd, benefitBeginDt, benefitResumeDt, customCoverageDesc, claimSystemCd, payments = [], lastWorkedDt, } = claim;
                const postalAddress = claim?.insuredDetails?.postalAddresses?.[0] || {};
                const claimData = {
                    fullName: claimantDisplayName,
                    firstName: claimantFirstName,
                    lastName: claimantLastName,
                    employeeId,
                    taxIdentificationNumber,
                    groupAgreementId,
                    divisionId,
                    eligibilityGroup,
                    organizationUnitId,
                    coverageType: claimCoverageTypeCd,
                    claimId,
                    legacyClaimNumber: aliasClaimId,
                    choiceCd,
                    disabilityDate: RemoveYear(disabilityBeginDt),
                    workState: employeeWorkStateCd,
                    benefitResumeDt,
                    claimBenefitBeginDt: benefitBeginDt,
                    coverageSourceDesc: customCoverageDesc,
                    addressLineOne: postalAddress?.addressLineOne || null,
                    addressLineTwo: postalAddress?.addressLineTwo || null,
                    city: postalAddress?.city || null,
                    jurisdictionCode: postalAddress?.jurisdictionCode || null,
                    postalCode: postalAddress?.postalCd || null,
                };
                if (!payments.length)
                    return claimData;
                return payments.flatMap((payment) => {
                    const { payeeType, checkNumber, paymentAmt, paymentIssueDt, paymentPeriodBeginDt, paymentPeriodEndDt, paymentStatus, nonPayableBeginDt, nonPayableEndDt, paymentTransmitMethod, payeeFirstName, payeeFullName, payeeLastName, claimPaymentSummary = {}, paymentComponents = [], } = payment;
                    const eligiblePaymentComponents = claimSystemCd === "FNEOS"
                        ? paymentComponents.filter(isMappedTableComponent)
                        : paymentComponents.filter(isDstComponent);
                    const componentMap = buildComponentMap(eligiblePaymentComponents);
                    const maxInstances = getMaxComponentInstances(componentMap);
                    // Create multiple rows if there are duplicate componentIds
                    return Array.from({ length: maxInstances }, (_, index) => {
                        const components = Object.fromEntries(Object.entries(PAYMENT_COMPONENT_IDS).map(([key, ids]) => {
                            const foundComponents = findComponent(componentMap, ids);
                            return [
                                key,
                                foundComponents && foundComponents[index]
                                    ? foundComponents[index]
                                    : null,
                            ];
                        }));
                        const { employeeSocialSecurityTaxAmt, federdalWithHoldingTaxAmt, stateWithHoldingTaxAmt, socialSecurityOffsetAmt, grossBenefitAmt, localWithHoldingTaxAmt, stateOffsetAmt, dentalInsurancePremiumAmt, overReductionPaymentAmt, postTaxTotalAmt, preTaxMedicalInsTotalAmt, preTaxTotalAmt, workersCompensationOffsetAmt, allOtherOffsetAmt, employerSocialSecurityTaxAmt, medicareTaxAmt, paymentDeductionsAmt, paymentOffsetsAmt } = claimPaymentSummary;
                        return {
                            ...claimData,
                            payeeType: payeeType,
                            employeeSSTax: employeeSocialSecurityTaxAmt,
                            federalWithholding: federdalWithHoldingTaxAmt,
                            stateWithholding: stateWithHoldingTaxAmt,
                            checkEftNumber: checkNumber,
                            paymentAmount: paymentAmt,
                            paymentDate: paymentIssueDt,
                            paymentStartDate: paymentPeriodBeginDt,
                            paymentEndDate: paymentPeriodEndDt,
                            paymentStatus: paymentStatus,
                            socialSecurityOffsetAmt: socialSecurityOffsetAmt,
                            grossBenefitInPeriod: grossBenefitAmt,
                            nonPaymentStartDt: nonPayableBeginDt,
                            nonPaymentEndDt: nonPayableEndDt,
                            localWithholding: localWithHoldingTaxAmt,
                            stateOffsetAmt: stateOffsetAmt,
                            dentalInsurancePremiumAmt: dentalInsurancePremiumAmt,
                            overpaymentReduction: overReductionPaymentAmt,
                            postTaxTotal: postTaxTotalAmt,
                            preTaxMedicalInsurance: preTaxMedicalInsTotalAmt,
                            preTaxTotal: preTaxTotalAmt,
                            paymentType: paymentTransmitMethod,
                            wcOffset: workersCompensationOffsetAmt,
                            otherOffsetsAmt: allOtherOffsetAmt,
                            employerSSTax: employerSocialSecurityTaxAmt,
                            medicareTax: medicareTaxAmt,
                            erMedicareTax: medicareTaxAmt,
                            paymentGrossBenefitAmt: grossBenefitAmt,
                            paymentDeductionsAmt: paymentDeductionsAmt,
                            payeeFirstName: payeeFirstName,
                            payeeFullName: payeeFullName,
                            payeeLastName: payeeLastName,
                            paymentOffsetsAmt: paymentOffsetsAmt,
                            "401K Deferral": getAmt(components.pc401kDeferral),
                            "Accidental Death & Dismemberment": getAmt(components.accidentalDeathDismemberment),
                            "Pre Tax Dental": getAmt(components.preTaxDental),
                            "Post-tax Cancer": getAmt(components.postTaxCancer),
                            Retirement: getAmt(components.retirement),
                            "Pre Tax Flex Spen Daycare": getAmt(components.preTaxFlexSpenDaycare),
                            "Health Savings Account": getAmt(components.healthSavingsAccount),
                            "Group Supplemental Employee Life": getAmt(components.groupSupplementalEmployeeLife),
                            "Pre Tax Group Supp Dependent Life": getAmt(components.preTaxGroupSuppDependentLife),
                            "Pre Tax Flex Spen Healthcare": getAmt(components.preTaxFlexSpenHealthcare),
                            "Premium Contribution": getAmt(components.premiumContribution),
                            "Pre-tax Cancer Plan": getAmt(components.preTaxCancerPlan),
                            Garnishment: getAmt(components.garnishment),
                            "Post-Tax Group Legal Services": getAmt(components.postTaxGroupLegalServices),
                            "Post-Tax Vision": getAmt(components.postTaxVision),
                            "Pre-Tax Medical": getAmt(components.preTaxMedical),
                            "Pre-Tax Vision": getAmt(components.preTaxVision),
                            "Post Tax Primary Dental Premium": getAmt(components.dentalInsurancePremium),
                            "General Post Tax Deduction": getAmt(components.generalPostTaxDeduction),
                            "General Pre Tax Deduction": getAmt(components.generalPreTaxDeduction),
                            "Post-Tax Medical": getAmt(components.postTaxMedical),
                            "Post-Tax Dependent Life Ins Premium": getAmt(components.postTaxDependentLifeInsPremium),
                            "Post-Tax Long Term Disability Premium": getAmt(components.postTaxLongTermDisabilityPremium),
                            "Pre-Tax Medical Flexible Spending Account": getAmt(components.preTaxMedicalFlexibleSpendingAccount),
                            "Post-Tax Voluntary Life - Spouse Premium": getAmt(components.postTaxVoluntaryLifeSpousePremium),
                            "Post-Tax Voluntary Life - Employee Premium": getAmt(components.postTaxVoluntaryLifeEmployeePremium),
                            "Post-Tax Personal Accident Ins Premium": getAmt(components.postTaxPersonalAccidentInsPremium),
                            "Pre-Tax Long Term Care": getAmt(components.preTaxLTC),
                            "Post-Tax Long Term Care": getAmt(components.postTaxLTC),
                            postTaxOtherADDPremium: getAmt(components.postTaxOtherADDPremium),
                            postTaxPrimaryLifeInsPremium: getAmt(components.postTaxPrimaryLifeInsPremium),
                            preTaxDependentCareFlexSpendingAccount: getAmt(components.preTaxDependentCareFlexSpendingAmt),
                            disabilityBeginDt: RemoveYear(disabilityBeginDt),
                            lastDateWorked: RemoveYear(lastWorkedDt),
                        };
                    });
                });
            });
            return { claimsPaymentSearch, searchResultMetadata };
        };
    }
}
export default new ClaimsPaymentSearch();
